/**
 * Access template.
 */
import AccessContainer from '../atoms/container/accessContainer';
import AccessButtonContainer from '../atoms/container/accessButtonContainer';
import AccessTextBlockContainer from '../atoms/container/accessTextBlockContainer';
import AccessTextDescription from '../atoms/text/accessTextDescription';
import AccessTextId from '@components/atoms/text/accessTextId';
import AccessTextTitle from '../atoms/text/accessTextTitle';
import ConfirmButton from '../atoms/buttons/confirmButton';
import Languages from '../../../shared/types/enums/languages';
import LanguageContext from '../../../shared/components/utils/context/languageContext';
import LanguagesList from '../sections/list/languagesList/languagesList';
import QRCode from '../sections/qr/qrCode';
import TranslationManager from '../../../shared/assets/locale/translationsManager';
import useKeyboard from '../../../shared/components/utils/hooks/useKeyboard';
import { selectMagicRemoteEnabled } from '../../../shared/store/slices/magicRemote.slice';
import { useAppSelector } from '../../../shared/store/hooks';
import { DELAY } from '../../../shared/types/constants/animations';

/**
 * Props interface for the access template.
 */
interface Props {
  buttonText: string | null;
  description: string;
  idDevice?: string;
  languages?: Languages[];
  title: string;
  qrUrl?: string;
  action: () => void;
  returnAction?: () => void;
}

/**
 * Displays a view with different elements such as a title, description, QR code, ID device, and buttons.
 */
export default function AccessTemplate({
  buttonText,
  description,
  idDevice,
  languages,
  title,
  qrUrl,
  action,
  returnAction,
}: Props) {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  const enabled = useAppSelector(selectMagicRemoteEnabled);

  // ------------------------------------------------------------
  // Callbacks
  // ------------------------------------------------------------

  const setLanguage = (language?: Languages) => {
    language && TranslationManager.setLanguage(language);
  };

  // ------------------------------------------------------------
  // Keyboard hooks
  // ------------------------------------------------------------

  useKeyboard({
    canBeFocused: false,
    stopPropagation: true,
    keyDownHandler: (key) => {
      if (key === 'SELECT') {
        action();
      } else if (key === 'BACK' && returnAction) {
        returnAction();
      }
    },
  });

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  return (
    <AccessContainer delay={DELAY.ACCESS_OPACITY}>
      <AccessTextBlockContainer>
        <AccessTextTitle>{title}</AccessTextTitle>
        <AccessTextDescription
          dangerouslySetInnerHTML={{ __html: description }}
        />
        {qrUrl ? <QRCode url={qrUrl} /> : null}
        {idDevice ? <AccessTextId>{`ID: ${idDevice}`}</AccessTextId> : null}
      </AccessTextBlockContainer>
      {buttonText ? (
        <AccessButtonContainer>
          <ConfirmButton
            focused={!enabled}
            width={480}
            height={75}
            onClick={action}
          >
            {buttonText}
          </ConfirmButton>
        </AccessButtonContainer>
      ) : null}
      {languages ? (
        <LanguageContext.Provider value={{ setLanguage }}>
          <LanguagesList languages={languages} />
        </LanguageContext.Provider>
      ) : null}
    </AccessContainer>
  );
}
